import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Card, CardContent } from '@/components/ui/card';
import { SemiCircleGauge } from '@/components/ui/SemiCircleGauge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RiskGauges = ({ stats }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(null); // 'clientes-inactivos' | 'sin-ofertas'
  const [detailData, setDetailData] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const navigate = useNavigate();

  const handleIndicatorClick = async (type) => {
    setDialogType(type);
    setDialogOpen(true);
    setDetailLoading(true);
    setDetailData([]);

    try {
      const res = await axios.get(`${API}/stats/detalle/${type}`);
      setDetailData(res.data.items || []);
    } catch (err) {
      console.error('Error loading detail:', err);
      toast.error('Error al cargar detalle del indicador');
    } finally {
      setDetailLoading(false);
    }
  };

  const dialogConfig = {
    'clientes-inactivos': {
      title: 'Clientes Inactivos (30 días)',
      description: 'Clientes activos que no enviaron mensajes por WhatsApp en los últimos 30 días.',
      columns: [
        { key: 'nombre', label: 'Nombre' },
        { key: 'empresa', label: 'Empresa' },
        { key: 'ciudad', label: 'Ciudad' },
        { key: 'whatsapp', label: 'WhatsApp' },
      ],
      emptyMessage: 'Todos los clientes tienen actividad reciente 🎉',
      onRowClick: (item) => navigate(`/clientes`),
    },
    'sin-ofertas': {
      title: 'Publicaciones sin Ofertas',
      description: 'Publicaciones en estado "Enviada" que aún no recibieron ninguna oferta.',
      columns: [
        { key: 'id', label: 'ID' },
        { key: 'lote', label: 'Vehículo', render: (item) => `${item.tipoGanado} ${item.categoria} ${item.numeroLote}` },
        { key: 'precioBase', label: 'Precio Base', render: (item) => item.precioBase ? `$${item.precioBase.toLocaleString()}` : '-' },
        { key: 'createdAt', label: 'Fecha', render: (item) => item.createdAt ? format(new Date(item.createdAt), "dd MMM yyyy", { locale: es }) : '-' },
        { key: 'autor', label: 'Autor' },
      ],
      emptyMessage: 'Todas las publicaciones tienen ofertas 🎉',
      onRowClick: (item) => navigate(`/publicaciones/${item.id}`),
    },
  };

  if (!stats) return null;

  const currentConfig = dialogConfig[dialogType] || {};

  return (
    <>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* Publicaciones Vencidas — NO clickable */}
          <Card className="bg-card border border-border hover:border-border/80 transition-all duration-300 shadow-lg group">
            <CardContent className="pt-6 relative">
              <div className="absolute top-3 right-3 flex h-2.5 w-2.5">
                {stats.publicaciones_vencidas > 0 && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>}
                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${stats.publicaciones_vencidas >= 5 ? 'bg-red-500' : stats.publicaciones_vencidas >= 3 ? 'bg-yellow-500' : 'bg-green-500'}`}></span>
              </div>
              <SemiCircleGauge 
                value={stats.publicaciones_vencidas || 0}
                label="Publicaciones Vencidas"
                max={10}
                targetRef="vencidas"
                gradients={[
                  { offset: "0%", color: "#22c55e" },
                  { offset: "30%", color: "#eab308" },
                  { offset: "100%", color: "#ef4444" }
                ]}
              />
            </CardContent>
          </Card>

          {/* Sin Ofertas — clickable */}
          <Card 
            className="bg-card border border-border hover:border-border/80 transition-all duration-300 shadow-lg group cursor-pointer hover:shadow-xl hover:scale-[1.02]"
            onClick={() => handleIndicatorClick('sin-ofertas')}
          >
            <CardContent className="pt-6 relative">
              <div className="absolute top-3 right-3 flex h-2.5 w-2.5">
                {(stats.sin_ofertas_enviadas || 0) > 0 && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-60"></span>}
                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${stats.sin_ofertas_enviadas >= 10 ? 'bg-red-500' : stats.sin_ofertas_enviadas >= 5 ? 'bg-yellow-500' : 'bg-green-500'}`}></span>
              </div>
              <SemiCircleGauge 
                value={stats.sin_ofertas_enviadas || 0}
                label="Sin Ofertas (Enviadas)"
                max={20}
                targetRef="riesgo"
                gradients={[
                  { offset: "0%", color: "#22c55e" },
                  { offset: "40%", color: "#eab308" },
                  { offset: "100%", color: "#ef4444" }
                ]}
              />
              <div className="text-[10px] text-center text-primary/60 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Click para ver detalle
              </div>
            </CardContent>
          </Card>

          {/* Salud de Entrega — NO clickable */}
          <Card className="bg-card border border-border hover:border-border/80 transition-all duration-300 shadow-lg group">
            <CardContent className="pt-6 relative">
              <div className="absolute top-3 right-3 flex h-2.5 w-2.5">
                {stats.salud_entrega && stats.salud_entrega < 90 && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-50"></span>}
                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${stats.salud_entrega >= 90 ? 'bg-green-500' : stats.salud_entrega >= 80 ? 'bg-yellow-500' : 'bg-red-500'}`}></span>
              </div>
              <SemiCircleGauge 
                value={stats.salud_entrega || 0}
                label="Salud Entrega (%)"
                max={100}
                targetRef="salud_entrega"
                gradients={[
                  { offset: "0%", color: "#ef4444" },
                  { offset: "70%", color: "#eab308" },
                  { offset: "100%", color: "#22c55e" }
                ]}
              />
            </CardContent>
          </Card>

          {/* Clientes Inactivos — clickable */}
          <Card 
            className="bg-card border border-border hover:border-border/80 transition-all duration-300 shadow-lg group cursor-pointer hover:shadow-xl hover:scale-[1.02]"
            onClick={() => handleIndicatorClick('clientes-inactivos')}
          >
            <CardContent className="pt-6 relative">
              <div className="absolute top-3 right-3 flex h-2.5 w-2.5">
                {(stats.clientes_inactivos || 0) > 0 && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-50"></span>}
                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${stats.clientes_inactivos >= 20 ? 'bg-red-500' : stats.clientes_inactivos >= 10 ? 'bg-yellow-500' : 'bg-green-500'}`}></span>
              </div>
              <SemiCircleGauge 
                value={stats.clientes_inactivos || 0}
                label="Clientes Inactivos (30d)"
                max={50}
                targetRef="inactivos"
                gradients={[
                  { offset: "0%", color: "#22c55e" },
                  { offset: "40%", color: "#eab308" },
                  { offset: "100%", color: "#ef4444" }
                ]}
              />
              <div className="text-[10px] text-center text-primary/60 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Click para ver detalle
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{currentConfig.title}</DialogTitle>
            <DialogDescription>{currentConfig.description}</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto mt-4">
            {detailLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Cargando...
                </div>
              </div>
            ) : detailData.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
                {currentConfig.emptyMessage}
              </div>
            ) : (
              <>
                <div className="text-xs text-muted-foreground mb-3 px-1">
                  {detailData.length} {detailData.length === 1 ? 'resultado' : 'resultados'}
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      {currentConfig.columns?.map((col) => (
                        <TableHead key={col.key}>{col.label}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detailData.map((item, idx) => (
                      <TableRow
                        key={item.id || idx}
                        className={currentConfig.onRowClick ? 'cursor-pointer hover:bg-muted/80' : ''}
                        onClick={() => {
                          if (currentConfig.onRowClick) {
                            setDialogOpen(false);
                            currentConfig.onRowClick(item);
                          }
                        }}
                      >
                        {currentConfig.columns?.map((col) => (
                          <TableCell key={col.key}>
                            {col.render ? col.render(item) : (item[col.key] || '-')}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
